import {Storage as Store} from '@capacitor/storage';
import store from "@/store/index";
import axios from "axios";
import {responses} from "@/utils/responses";
import router from "@/router";
import {nextTick} from 'vue'

export const ZRA_DETAILS = 'zra_details';
export const DEFAULT_STORE = 'default_store';
export const ALL_STORES = 'all_stores';
export const HAS_LOYALTY = 'hasLoyalty';
export const FRANCHISE_SETTINGS = 'franchise_settings';
export const REGISTRATION_DETAILS = 'registration_details';
/**
 * Storage key for the deeplink session minted by
 * {@code POST /posAuth/{DB}/login} on Franchise V2 (Franchise Online
 * Server V2 / port 7082). Carries only an opaque {@code sessionId}
 * and {@code sessionExpiresAt} - never the user's plaintext password.
 * The legacy {@code ?auth=} deeplink payload was base64-encoded JSON
 * containing the password, so the URL itself was the credential. See
 * {@code docs/exec-plans/active/2026-06-22-deeplink-session-rollout.md}
 * for the rollout plan.
 */
export const CURRENT_SESSION = 'current_session';

export const ADMIN = 'admin';
export const CATEGORIES = 'categories';
export const AllUsers = 'all_users';
export const AllSecurities = 'securities';
export const CurrentUser = 'current_user';
export const STOCK = 'stock';
export const STOCK_IMAGES = 'stockImages';
export const PAYMENT_METHODS = 'payment_methods';
export const STOCK_SETTING = 'stock_setting';

export const HASKAZANG = 'hasKazang';
export const KAZANGSESSION = 'kazangSession';
export const KAZANGPRODUCTS = 'kazangProductList';
export const TAX_LABELS = 'taxLabels';

export const FLOAT = 'float';
export const DEVICE_SETTINGS = 'device_settings';
export const SAVED_TABLES = 'saved_tables';

export const BASE_CURRENCY = 'base_currency';

export const DISCOUNT_SPECIALS = 'discountSpecials';
export const MULTI_SPECIALS = 'multiSpecials';
export const COMBO_SPECIALS = 'comboSpecials';
export const ZED_MOBILE_STATUS = 'zedMobileStatus';
export const SUPERVISOR = 'supervisor'

const notEmpty = (val) => {
    if (!val) return false;
    return val !== '';
};

const encryptAdmin = (val) => {
    return window.btoa(val);
};

const decryptAdmin = (val) => {
    if (!val) return undefined;
    if (val.toString().charAt(0) === `"`) {
        val = val.toString().slice(1, -1);
    }
    return JSON.parse(window.atob(val));
};

function isImageURL(url) {
    return new Promise(function (resolve, reject) {
        let img = new Image();
        img.onload = function () {
            resolve(true);
        };
        img.onerror = function () {
            resolve(false);
        };
        img.src = url;
    });
}

const getValue = async (key) => {
    // Fall back to Capacitor Storage
    let value = await Store.get({key});
    return value && value.value ? JSON.parse(value.value) : null;
};

const setValue = async (key, value) => {
    await Store.set({key, value : JSON.stringify(value)});
};

export const Storage = {
    logOutofStore: async () => {
        await Storage.setAdminUser(null)
        await Storage.setAllStores(null)
        await Storage.setAllUsers(null)
        await Storage.setCurrentUser(null)
        // Drop the deeplink session so a stale token cannot be
        // used to open BackOffice after logout.
        await Storage.clearCurrentSession()
    },
    getCurrentStoreDB: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);  // Check every 50ms
            });
        }
        let defaultStore = store.getters.getDefaultStore;
        if (defaultStore) {
            if (!defaultStore){
                axios.get(reportTomcatApi+`getFranchiseByDB?DB=${defaultStore.StoreDB}`)
                    .then(({data})=>store.commit(`setAdmin`,data))
            }
            return defaultStore.StoreDB;
        }
        if (!androidMode) {
            let storeDB;

            try {
                let response = await axios.get(hardwareApi + `utils/getStoreDB`);
                if (response.status === 404) {
                    responses.showWarning(`Request for hardware service update`)
                    return null;
                }
                storeDB = response.data;
                if (storeDB && storeDB !== 'none') {
                    console.trace(`Called`)
                    return axios.get(reportTomcatApi + `getStoresByDB?DB=${storeDB}`)
                        .then(async ({data}) => {
                            store.commit(`setRegisteredStores`, data);
                            let currentStore = data.find(s => s.StoreDB === storeDB);
                            if (currentStore) {
                                await store.commit(`setDefaultStore`, currentStore);
                                axios.get(reportTomcatApi+`getFranchiseByDB?DB=${storeDB}`)
                                    .then(({data})=>store.commit(`setAdmin`,data))
                                if (router.currentRoute.name === `login` || router.currentRoute.name === `poslogin` || router.currentRoute.name === `setup`) {
                                    router.push({name: `poslogin`})
                                }
                                return currentStore.StoreDB;
                            }
                        })
                }
            } catch (e) {
            }
        }
        return null;
    },
    setAdminUser: async (user) => {
        await Store.set({
            key: ADMIN, value: encryptAdmin(JSON.stringify(user)),
        });
        store.commit('setAdmin', user)
    },
    getAdminUser: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getAdmin;
        if (value) return value;
        value = await Store.get({
            key: ADMIN,
        });
        // Fall back to Capacitor Storage
        value = (value && value.value) ? value.value : null;
        return decryptAdmin(value);
    },

    setAllStores: async (payload) => {
        await setValue(ALL_STORES, payload);
        store.commit('setRegisteredStores', payload);
    },
    getAllStores: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getRegisteredStores;
        if (value) return value;
        value = await getValue(ALL_STORES);
        return value;
    },

    setFranchiseSettings: async (payload) => {
        await setValue(FRANCHISE_SETTINGS, payload);
        store.commit('setFranchiseSettings', payload);
    },
    getFranchiseSettings: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getFranchiseSettings;
        if (value) return value;
        value = await getValue(FRANCHISE_SETTINGS);
        return value;
    },

    setRegistrationDetails: async (payload) => {
        await setValue(REGISTRATION_DETAILS, payload);
        store.commit('setRegistrationDetails', payload);
    },
    getRegistrationDetails: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getRegistrationDetails;
        if (value) return value;
        value = await getValue(REGISTRATION_DETAILS);
        return value;
    },

    setZRADetails: async (payload) => {
        await setValue(ZRA_DETAILS, payload);
    },
    getZRADetails: async () => {
        await Storage.waitForHydrate()
        return getValue(ZRA_DETAILS);
    },

    setCategories: async (categories) => {
        await setValue(CATEGORIES, categories);
        store.commit('setCategories', categories);
    },
    getCategories: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getCategories;
        if (value) return value;
        value = await getValue(CATEGORIES);
        return value;
    },

    setHasKazang: async (payload) => {
        store.commit('setHasKazang', payload);
    },
    getHasKazang: async () => {
        return true;
    },

    setDefaultStore: async (defaultStore) => {
        await setValue(DEFAULT_STORE, defaultStore);
        store.commit('setDefaultStore', defaultStore);
    },
    getDefaultStore: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);  // Check every 50ms
            });
        }
        let value = store.getters.getDefaultStore;
        if (value) {
            if (!value){
                axios.get(reportTomcatApi+`getFranchiseByDB?DB=${value.StoreDB}`)
                    .then(({data})=>store.commit(`setAdmin`,data))
            }
            setTimeout(() => {
                let name = router.currentRoute.name;
                if (name === 'login' || name === 'poslogin' || name === 'setup') {
                    router.push({name: 'poslogin'}).catch((err) => {
                        if (err.name !== 'NavigationDuplicated') {
                            console.error(err)
                        }
                    })
                }
            }, 0)
            return value;
        }
        value = await getValue(DEFAULT_STORE);
        if (value) {
            if (!value){
                axios.get(reportTomcatApi+`getFranchiseByDB?DB=${value.StoreDB}`)
                    .then(({data})=>store.commit(`setAdmin`,data))
            }
            setTimeout(() => {
                let name = router.currentRoute.name;
                if (name === 'login' || name === 'setup') {
                    router.push({name: 'poslogin'}).catch((err) => {
                        if (err.name !== 'NavigationDuplicated') {
                            console.error(err)
                        }
                    })
                }
            }, 0)
            return value;
        }
        if (!androidMode) {
            let storeDB;
            try {
                let response = await axios.get(hardwareApi + `utils/getStoreDB`);
                if (response.status === 404) {
                    responses.showWarning(`Request for hardware service update`)
                    return null;
                }
                storeDB = response.data;
                if (storeDB && storeDB !== 'none') {
                    console.trace(`Called`)
                    return axios.get(reportTomcatApi + `getStoresByDB?DB=${storeDB}`)
                        .then(async ({data}) => {
                            store.commit(`setRegisteredStores`, data);
                            let currentStore = data.find(s => s.StoreDB === storeDB);
                            if (currentStore) {
                                await store.commit(`setDefaultStore`, currentStore);
                                axios.get(reportTomcatApi+`getFranchiseByDB?DB=${storeDB}`)
                                    .then(({data})=>store.commit(`setAdmin`,data))
                                setTimeout(() => {
                                    let name = router.currentRoute.name;
                                    if (name === 'login' || name === 'setup') {
                                        router.push({name: 'poslogin'}).catch((err) => {
                                            if (err.name !== 'NavigationDuplicated') {
                                                console.error(err)
                                            }
                                        })
                                    }
                                }, 0)

                                return currentStore;
                            }
                        })
                }
            } catch (e) {
            }
        }
        return null;
    },

    getCurrentStoreID: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getFranchiseSettings;
        if (value) return value.StoreId;
        value = await getValue(FRANCHISE_SETTINGS);
        if (!value) return null;
        return value.StoreId;
    },

    setAllUsers: async (users) => {
        await setValue(AllUsers, users);
        store.commit('setAllUsers', users);
    },
    getAllUsers: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getUsers;
        if (value) return value;
        value = await getValue(AllUsers);
        return value;
    },

    setAllSecurities: async (payload) => {
        await setValue(AllSecurities, payload);
        store.commit('setAllSecurities', payload);
    },
    getAllSecurities: async () => {
        let value = store.getters.getSecurities;
        if (value) return value;
        value = await getValue(AllSecurities);
        return value;
    },

    setCurrentUser: async (currentUser) => {
        await setValue(CurrentUser, currentUser);
        store.commit('setCurrentUser', currentUser);
    },
    getCurrentUser: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getCurrentUser;
        if (value) return value;
        value = await getValue(CurrentUser);
        return value;
    },

    setStockImages: async (payload) => {
        let images = [];
        for (let i = 0; i < payload.length; i++) {
            let itemImage = payload[i];
            let isValid = await isImageURL(itemImage.Image);
            if (isValid) {
                images.push(itemImage);
            }
        }
        await setValue(STOCK_IMAGES, images)
    },
    getAllStockImages: async () => {
        await Storage.waitForHydrate()
        return getValue(STOCK_IMAGES) ?? [];
    },

    setStock: async (stock) => {
        await setValue(STOCK, stock);
    },
    getAllStock: async () => {
        await Storage.waitForHydrate()
        let stock = await getValue(STOCK) ?? [];
        let categories = await getValue(CATEGORIES);
        categories = categories.map((c) => c.name);
        let images = await Storage.getAllStockImages();
        stock = stock.filter((item) => {
            let department = item.Department.toLowerCase();
            return categories.includes(department);
        }).map((item) => {
            let image = images.find((stockItem) => stockItem.PLU === item.PLU);
            if (image) {
                item.Image = image.Image;
            }
            return item;
        });
        return stock;
    },

    getByPlu: async (plu) => {
        await Storage.waitForHydrate()
        let stock = await getValue(STOCK) ?? [];
        let item = stock.find((item) => item.PLU === plu);
        let images = await Storage.getAllStockImages();
        let image = images.find((stockItem) => stockItem.PLU === item.PLU);
        if (image) {
            item.Image = image.Image;
        }
        return item;
    },

    getFastLine: async () => {
        await Storage.waitForHydrate()
        let stock = await getValue(STOCK) ?? [];
        let images = await Storage.getAllStockImages();
        stock = stock.map((item) => {
            let image = images.find((stockItem) => stockItem.PLU === item.PLU);
            if (image) {
                item.Image = image.Image;
            }
            return item;
        });
        return stock.filter((sc) => sc.FastLine === 1);
    },

    searchStockDesc: async (query) => {
        if (notEmpty(query.category)) {
            return (await Storage.searchStock(query))
                .filter(
                    (s) =>
                        s.Description.toLowerCase().includes(query.description.toLowerCase()) ||
                        s.Description2?.toLowerCase().includes(query.description.toLowerCase()) ||
                        s.PLU.toLowerCase().includes(query.description.toLowerCase()) ||
                        s.barcode.toLowerCase().includes(query.description.toLowerCase())
                )
                .sort((a, b) => {
                    return a.Description < b.Description;
                });
        }
        return (await Storage.getAllStock())
            .filter(
                (s) =>
                    s.Description.toLowerCase().includes(query.description.toLowerCase()) ||
                    s.Description2?.toLowerCase().includes(query.description.toLowerCase()) ||
                    s.PLU.toLowerCase().includes(query.description.toLowerCase()) ||
                    s.barcode.toLowerCase().includes(query.description.toLowerCase())
            )
            .sort((a, b) => {
                return a.Description < b.Description;
            });
    },

    searchStock: async (query) => {
        let stock = await getValue(STOCK) ?? [];
        let result = [];
        if (query.category !== '' && query.subCategory !== '' && query.sub2Category !== '') {
            result = stock.filter((s) => s.Department.toLowerCase() === query.category.toLowerCase())
                .filter((s2) => s2.SubDep.toLowerCase() === query.subCategory.toLowerCase())
                .filter((s3) => s3.Subdep2.toLowerCase() === query.sub2Category.toLowerCase());
        } else if (query.category !== '' && query.subCategory !== '' && query.sub2Category === '') {
            result = stock.filter((s) => s.Department.toLowerCase() === query.category.toLowerCase())
                .filter((s2) => s2.SubDep.toLowerCase() === query.subCategory.toLowerCase());
        } else if (query.category !== '' && query.sub2Category === '' && query.subCategory === '') {
            result = stock.filter((s) => s.Department.toLowerCase() === query.category.toLowerCase());
        }
        if (query.description) {
            return result.filter(
                (s) =>
                    s.Description.toLowerCase().includes(query.description.toLowerCase()) ||
                    s.PLU.toLowerCase().includes(query.description.toLowerCase()) ||
                    s.barcode.toLowerCase().includes(query.description.toLowerCase())
            );
        }
        let images = await Storage.getAllStockImages();
        result = result.map((item) => {
            let image = images.find((stockItem) => stockItem.PLU === item.PLU);
            if (image) {
                item.Image = image.Image;
            }
            return item;
        });
        return result.sort((a, b) => {
            return a.Description < b.Description;
        });
    },

    setFloat: async (payload) => {
        await setValue(FLOAT, payload)
        store.commit('setUserFloat', payload);
    },
    getFloat: async () => {
        let value = store.getters.getFloats;
        if (value) return value;
        value = await getValue(FLOAT);
        return value;
    },

    setDeviceSettings: async (payload) => {
        await setValue(DEVICE_SETTINGS, payload)
        store.commit('setDeviceSettings', payload)
    },

    getDeviceSettings: async () => {
        let value = store.getters.getDeviceSettings;
        if (value) return value;
        value = await getValue(DEVICE_SETTINGS);
        return value;
    },

    getAllTables: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getAllTables;
        if (value) return value;
        value = await getValue(SAVED_TABLES);
        return value;
    },

    setTables: async (payload) => {
        await setValue(SAVED_TABLES, payload)
    },

    createTable: async (payload) => {
        let value = await Store.get({
            key: SAVED_TABLES,
        });
        if (!value) {
            value = {
                value: JSON.stringify([]),
            };
        }
        let tables = JSON.parse(value.value);
        if (!tables) {
            tables = [];
        }
        tables.push(payload);
        await Store.set({
            key: SAVED_TABLES,
            value: JSON.stringify(tables),
        });
    },

    editTable: async (payload) => {
        const value = await Store.get({
            key: SAVED_TABLES,
        });
        let tables = JSON.parse(value.value);
        tables = tables.map((table) => {
            if (table.SaleNum === payload.SaleNum) {
                return payload;
            }
            return table;
        });
        await Store.set({
            key: SAVED_TABLES,
            value: JSON.stringify(tables),
        });
    },

    deleteTable: async (salenum) => {
        const value = await Store.get({
            key: SAVED_TABLES,
        });
        let tables = JSON.parse(value.value);
        tables = tables.filter((table) => table.SaleNum !== salenum);
        await Store.set({
            key: SAVED_TABLES,
            value: JSON.stringify(tables),
        });
    },

    clear: async () => Store.clear(),

    setStockSettings: async (stock) => {
        await setValue(STOCK_SETTING, stock);
        store.commit('setStockSetting', stock);
    },
    getStockSetting: async (plu) => {
        await Storage.waitForHydrate()
        let settings = store.getters.getStockSetting;
        if (settings) return settings.find(set => set.plu === plu);
        settings = await getValue(STOCK_SETTING)
        return settings.find(set => set.plu === plu);
    },

    setPaymentMethods: async (data) => {
        await setValue(PAYMENT_METHODS, data)
        store.commit('setPaymentMethods', data);

    },
    getPaymentMethods: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getPaymentMethods;
        if (value) return value;
        value = await getValue(PAYMENT_METHODS);
        return value;
    },

    setKazangSession: async (data) => {
        await setValue(KAZANGSESSION, data)
        store.commit('setKazangSession', data);
    },
    getKazangSession: async () => {
        let value = store.getters.getKazangSessionID;
        if (value) return value;
        value = await getValue(KAZANGSESSION);
        return value;
    },

    setKazangProductList: async (data) => {
        await setValue(KAZANGPRODUCTS, data)
        store.commit('setKazangProductList', data);
    },
    getKazangProductList: async () => {
        let value = store.getters.getKazangProductList;
        if (value) return value;
        value = await getValue(KAZANGPRODUCTS);
        return value;
    },

    setBaseCurrency: async (data) => {
        await setValue(BASE_CURRENCY, data)
        store.commit('setBaseCurrency', data);
    },
    getBaseCurrency: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getBaseCurrency;
        if (value) return value;
        value = await getValue(BASE_CURRENCY);
        return value;
    },

    setDiscountSpecials: async (payload) => {
        setValue(DISCOUNT_SPECIALS, payload)
        store.commit('setDiscountSpecials', payload);
    },
    getDiscountSpecials: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getDiscountSpecials;
        if (value) return value;
        value = await getValue(DISCOUNT_SPECIALS);
        return value;
    },

    setHasLoyalty: async (payload) => {
        setValue(HAS_LOYALTY, payload)
    },
    getHasLoyalty: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getHasLoyalty;
        if (value) return value;
        value = await getValue(HAS_LOYALTY);
        return value;
    },

    setMultiSpecials: async (payload) => setValue(MULTI_SPECIALS, payload),
    getMultiSpecials: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getMultiSpecials;
        if (value) return value;
        value = await getValue(MULTI_SPECIALS);
        return value;
    },

    setZedMobileStatus: async (payload) => setValue(ZED_MOBILE_STATUS, payload),
    getZedMobileStatus: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getZedMobileStatus;
        if (value) return value;
        value = await getValue(ZED_MOBILE_STATUS);
        return value;
    },

    setComboSpecials: async (payload) => setValue(COMBO_SPECIALS, payload),
    getComboSpecials: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getComboSpecials;
        if (value) return value;
        value = await getValue(COMBO_SPECIALS);
        return value;
    },

    setTaxLabels: async (payload) => {
        setValue(TAX_LABELS, payload)
        store.commit('setTaxLabels', payload);
    },
    getAllTaxLabels: async () => {
        await Storage.waitForHydrate()
        let value = store.getters.getTaxLabels;
        if (value) return value;
        value = await getValue(TAX_LABELS);
        return value;
    },

    
    setSupervisor: async (supervisor) => {
        await setValue(SUPERVISOR, supervisor);
        store.commit('setSupervisor', supervisor);
    },

    getSupervisor: async () => {
        let value = store.getters.getSupervisor;
        if (value) return value;
        value = await getValue(SUPERVISOR);
        return value;
    },

    /**
     * Persist the deeplink session payload returned by
     * {@code POST /posAuth/{DB}/login}. The session is scoped to the
     * given storeDB so a second-store login on the same device does
     * not stomp on the first store's session.
     *
     * <p>Payload shape (subset used by the backoffice-module deeplink
     * opener):
     * <pre>
     * {
     *   sessionId:        string  // 64-char hex from SecureRandom
     *   sessionExpiresAt: string  // ISO-8601 datetime
     *   username:         string  // emp.Username
     *   empId:            string  // emp.id
     * }
     * </pre>
     *
     * @param {Object} payload session payload from the login response
     * @param {string} storeDb the StoreDB this session was issued for
     */
    setCurrentSession: async (payload, storeDb) => {
        const value = Object.assign({}, payload || {}, { storeDb: storeDb || null });
        await setValue(CURRENT_SESSION, value);
    },

    /**
     * Read the persisted deeplink session, but only if it was minted
     * for the requested {@code storeDb}. A stale session from a
     * previous store is returned as null so the caller can request a
     * fresh one via {@code posLogin}.
     *
     * @param {string} expectedStoreDb the StoreDB the session must match
     * @returns {Promise<?Object>} the session payload or null when absent/stale
     */
    getCurrentSession: async (expectedStoreDb) => {
        const value = await getValue(CURRENT_SESSION);
        if (!value) return null;
        if (expectedStoreDb && value.storeDb && value.storeDb !== expectedStoreDb) {
            return null;
        }
        return value;
    },

    /**
     * Clear the persisted deeplink session. Called on logout / store
     * switch to prevent a stale token being used to open BackOffice.
     */
    clearCurrentSession: async () => {
        await Store.remove({ key: CURRENT_SESSION });
    },

    waitForHydrate: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);  // Check every 50ms
            });
        }
    }

};
